import type { FC } from 'react';
import classNames from 'classnames';

import CharacterIcon from 'app/icons/character.svg';
import TennisBallIcon from 'app/icons/tennis_ball.svg';

import style from './style.module.scss';

interface Props {
  games: GameETennis[];
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  labels: string[];
}

const ETennisScoreboard: FC<Props> = ({ games, opponent1, opponent2, labels }) => {
  const sortedGames = [...games].sort((a, b) => a.position - b.position);
  const playedGames = sortedGames.filter(g => g.status !== 'not_started');
  const runningGame = sortedGames.find(g => g.status === 'running');
  const isRunning = !!runningGame;
  const isFinished =
    !runningGame && sortedGames.length > 0 && sortedGames.every(g => g.status === 'finished');
  const isNotStarted =
    sortedGames.length === 0 || sortedGames.every(g => g.status === 'not_started');

  const hasGameScore =
    isRunning && runningGame?.current_game_score && runningGame.current_game_score.length > 0;

  if (isNotStarted) {
    return (
      <div className={style.scoreboard}>
        <div className={style.preMatch}>
          <div className={style.playerSide}>
            <span className={style.playerName}>{opponent1?.name}</span>
          </div>
          <span className={style.vsLabel}>vs</span>
          <div className={classNames(style.playerSide, style.rightSide)}>
            <span className={style.playerName}>{opponent2?.name}</span>
          </div>
        </div>
      </div>
    );
  }

  const displayedSets = playedGames.length > 0 ? playedGames : sortedGames;
  const colCount = displayedSets.length + (hasGameScore ? 1 : 0);
  const gridStyle = { '--col-count': colCount } as React.CSSProperties;

  const getOppScore = (game: GameETennis, oppId: number | undefined) =>
    game.opponents.find(o => o.id === oppId)?.score;

  const getIngameCharacterName = (oppId: number | undefined) =>
    sortedGames
      .map(game => game.opponents.find(o => o.id === oppId)?.ingame_character_name)
      .find(Boolean);

  const gameScore1 = runningGame?.current_game_score?.find(g => g.id === opponent1?.id);
  const gameScore2 = runningGame?.current_game_score?.find(g => g.id === opponent2?.id);

  return (
    <div className={style.scoreboard} style={gridStyle}>
      <div className={style.columnHeaders}>
        <span />
        {displayedSets.map(game => {
          const isCurrent = isRunning && game.status === 'running';
          return (
            <span
              key={game.position}
              className={classNames(style.columnLabel, { [style.currentLabel]: isCurrent })}
            >
              {labels[game.position - 1]}
              {isCurrent && <span className={style.liveDot} />}
            </span>
          );
        })}
        {hasGameScore && <span />}
      </div>

      {[
        { matchOpp: opponent1, gameScore: gameScore1 },
        { matchOpp: opponent2, gameScore: gameScore2 }
      ].map(({ matchOpp, gameScore }, idx) => {
        const isServer = isRunning && runningGame?.server_id === matchOpp?.id;
        const characterName = getIngameCharacterName(matchOpp?.id);

        return (
          <div key={matchOpp?.id ?? idx} className={style.playerRow}>
            <div className={style.playerInfo}>
              {characterName && (
                <span className={style.teamName}>
                  <CharacterIcon className={style.characterIcon} />
                  {characterName}
                </span>
              )}
              <span className={style.playerName}>{matchOpp?.name}</span>
            </div>

            {displayedSets.map((game, setIdx) => {
              const score = getOppScore(game, matchOpp?.id);
              const otherOppId = idx === 0 ? opponent2?.id : opponent1?.id;
              const otherScore = getOppScore(game, otherOppId);
              const isCurrent = isRunning && game.status === 'running';
              const isLeading =
                score !== undefined && otherScore !== undefined && score > otherScore;

              return (
                <span
                  key={game.position}
                  className={classNames(style.setScore, {
                    [style.currentSet]: isCurrent,
                    [style.leading]:
                      isLeading && (isCurrent || isFinished || game.status === 'finished')
                  })}
                >
                  {isServer && setIdx === 0 && <TennisBallIcon className={style.tennisBallIcon} />}
                  {score !== undefined ? score : '-'}
                </span>
              );
            })}

            {hasGameScore && (
              <span
                className={classNames(style.gameScore, {
                  [style.gameScoreLeading]:
                    gameScore &&
                    gameScore2 &&
                    gameScore1 &&
                    (gameScore.score === 'adv' ||
                      (typeof gameScore.score === 'number' &&
                        typeof (idx === 0 ? gameScore2 : gameScore1)?.score === 'number' &&
                        gameScore.score > ((idx === 0 ? gameScore2 : gameScore1)?.score as number)))
                })}
              >
                {gameScore?.score === 'adv' ? 'Ad' : gameScore?.score ?? '-'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

ETennisScoreboard.displayName = 'ETennisScoreboard';

export default ETennisScoreboard;
